import { useRef, useState } from "react";
import { REPLAY } from "@/data/replayContent";

const fmt = (s) => {
  if (!s || isNaN(s)) return "00:00";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

export default function VideoPlayer() {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [pct, setPct] = useState(0);
  const [time, setTime] = useState({ cur: 0, dur: 0 });
  const [failed, setFailed] = useState(false);

  const toggle = () => {
    if (failed) return;
    const v = videoRef.current;
    if (v.paused) { v.play().catch(() => setFailed(true)); setPlaying(true); setStarted(true); }
    else { v.pause(); setPlaying(false); }
  };

  const onTime = () => {
    const v = videoRef.current;
    if (!v.duration) return;
    const p = (v.currentTime / v.duration) * 100;
    setPct(p);
    setTime({ cur: v.currentTime, dur: v.duration });
    window.__watchPct = Math.max(window.__watchPct || 0, Math.round(p));
  };

  const seek = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const v = videoRef.current;
    if (v.duration) v.currentTime = ((e.clientX - r.left) / r.width) * v.duration;
  };

  const fullscreen = () => videoRef.current?.requestFullscreen?.();

  return (
    <div className="glass-card overflow-hidden" data-testid="replay-video-player">
      <div className="relative aspect-video bg-black">
        <video
          ref={videoRef}
          src={REPLAY.videoUrl}
          className="w-full h-full object-contain"
          muted={muted}
          onTimeUpdate={onTime}
          onEnded={() => setPlaying(false)}
          onError={() => { setFailed(true); setPlaying(false); }}
          onClick={toggle}
          playsInline
          data-testid="replay-video-element"
        />
        {failed && (
          <div data-testid="video-error-fallback" className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6" style={{ background: "radial-gradient(circle at 50% 40%, rgba(217,98,43,0.14), rgba(8,8,8,0.97) 75%)" }}>
            <span className="font-mono text-[10px] tracking-[0.35em] text-[#D9622B]">REPLAY COMING SOON</span>
            <span className="max-w-sm text-xl font-black uppercase leading-tight text-[#F0ECE4]" style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}>The full training recording is being uploaded</span>
            <span className="text-[12px] text-white/40 max-w-xs">Check back shortly — or skip ahead and book your free 1:1 review below.</span>
          </div>
        )}
        {!started && (
          <button onClick={toggle} data-testid="video-poster-overlay" className="absolute inset-0 flex flex-col items-center justify-center gap-5 text-center px-6" style={{ background: "radial-gradient(circle at 50% 40%, rgba(217,98,43,0.16), rgba(8,8,8,0.96) 75%)" }}>
            <span className="font-mono text-[10px] tracking-[0.35em] text-[#D9622B]">FULL TRAINING REPLAY</span>
            <span className="max-w-sm text-xl sm:text-2xl font-black uppercase leading-tight text-[#F0ECE4]" style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}>{REPLAY.videoTitle}</span>
            <span className="w-20 h-20 rounded-full bg-[#D9622B] flex items-center justify-center text-black text-2xl pl-1 transition-transform hover:scale-105">▶</span>
            <span className="font-mono text-[9px] tracking-[0.25em] text-white/30">PLACEHOLDER VIDEO — FINAL RECORDING TO BE SWAPPED IN</span>
          </button>
        )}
      </div>
      <div className="flex items-center gap-3 px-4 py-3 border-t border-white/10">
        <button onClick={toggle} data-testid="video-play-btn" className="w-9 h-9 shrink-0 rounded-full bg-[#D9622B] text-black text-xs flex items-center justify-center hover:brightness-110 transition-all">
          {playing ? "❚❚" : "▶"}
        </button>
        <div onClick={seek} data-testid="video-progress-bar" className="relative flex-1 h-1.5 bg-white/10 cursor-pointer rounded-full">
          <div className="absolute inset-y-0 left-0 bg-[#D9622B] rounded-full" style={{ width: `${pct}%` }} />
        </div>
        <span className="font-mono text-[10px] text-white/40 tabular-nums shrink-0">{fmt(time.cur)} / {fmt(time.dur)}</span>
        <button onClick={() => setMuted(!muted)} data-testid="video-mute-btn" className="text-white/50 hover:text-white text-sm shrink-0">{muted ? "🔇" : "🔊"}</button>
        <button onClick={fullscreen} data-testid="video-fullscreen-btn" className="text-white/50 hover:text-white text-sm shrink-0">⛶</button>
      </div>
    </div>
  );
}
